import { useCallback, useRef, useState } from 'react';
import { IStory, IStoryContent, IStoryMargins, IPublication } from '@opengov/stories-shared/types';
import { fetchMergeTags } from '../utils/fetchMergeTags';
import {
  disableClickFromStoryContentWithEmbedV3,
  fetchProgressTrackerSettings,
  filterAndOrderStoryContents,
  generateBase64HTML
} from '@opengov/stories-shared/utils';
import { replaceStoryContentsWithMergeTagValues } from '@opengov/stories-shared/utils/mergeTag';
import { addWatermarkAndDownload } from '../utils/addWatermarkToPdf';
import { coreFeatureFlagEffects } from '@opengov/app-core/featureFlag';
import { FLAG_ADD_PROPOSALS_TO_STORIES } from '@opengov/stories-shared/constants';
import { ISettings } from '../../../../../types';
import { postJson } from '../../../../utils/fetchUtils';
import { useToast } from '../../../Toast/context/useToast.context';

interface IPdfExportResponse {
  pdf?: { type: 'Buffer'; data: number[] };
}

export type ExportStoryPdfParams = {
  story: IStory;
  storyContents: IStoryContent[];
  settings?: ISettings;
  displayDate?: boolean;
  displayPageNumber?: boolean;
  displayTitle?: boolean;
  enlargeFonts?: boolean;
  startingPage?: string;
  margins?: IStoryMargins;
  storyMarginsEnabled?: boolean;
  showHeader?: boolean;
  showFooter?: boolean;
  publication?: IPublication;
  watermark?: string | null;
};

export type ExportStoryPdfResult = {
  success: boolean;
};

export type UseExportStoryPdfOptions = {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
};

/**
 * Hook for exporting story to PDF
 */
export function useExportStoryPdf({ onSuccess, onError }: UseExportStoryPdfOptions = {}) {
  const controllerRef = useRef<AbortController | null>(null);
  const [isExporting, setIsExporting] = useState<boolean>(false);
  const { toggleToast } = useToast();

  const exportPdf = useCallback(
    async ({
      story,
      storyContents,
      settings,
      displayDate = false,
      displayPageNumber = false,
      displayTitle = false,
      enlargeFonts = false,
      startingPage = '1',
      margins,
      storyMarginsEnabled = true,
      showHeader = true,
      showFooter = true,
      publication,
      watermark = null
    }: ExportStoryPdfParams) => {
      // Abort any previous request
      if (controllerRef.current) {
        controllerRef.current.abort();
      }

      const controller = new AbortController();
      controllerRef.current = controller;
      setIsExporting(true);

      try {
        // Validate inputs
        if (!story || !story._id) {
          throw new Error('Invalid story data: missing story object or ID');
        }
        if (!storyContents?.length) {
          throw new Error('No story contents provided for export');
        }

        const { title, description, thumbnail, customCssCode, _id, entity_id, isFollowable } = story;
        const hasFootNotes = displayDate || displayTitle || displayPageNumber;

        // 1. Filter and order story contents
        let sortedStoryContents = filterAndOrderStoryContents(storyContents, {
          hideHeader: !showHeader,
          hideFooter: !showFooter
        });

        if (sortedStoryContents.length === 0) {
          throw new Error('No content sections found after filtering. Check header/footer visibility.');
        }

        // 2. Check feature flag for proposals content
        const flags = await coreFeatureFlagEffects.getFlags();
        const canAddProposalsContent = Boolean(flags && flags[FLAG_ADD_PROPOSALS_TO_STORIES]);

        // 3. Fetch and apply merge tags (conditionally)
        if (canAddProposalsContent) {
          const mergeTags = await fetchMergeTags(_id, entity_id);
          sortedStoryContents = replaceStoryContentsWithMergeTagValues(sortedStoryContents, mergeTags);
        }

        // 4. Disable click from embed content
        const sortedStoryContentsWithDisabledClick = disableClickFromStoryContentWithEmbedV3(sortedStoryContents);

        // 5. Fetch progress tracker settings
        const progressTrackerSettings = await fetchProgressTrackerSettings(entity_id);

        // 6. Get fonts from settings or use defaults
        const fonts: { headline: string; paragraph: string } = settings?.brand?.fonts || {
          headline: '',
          paragraph: ''
        };

        // 7. Generate base64 HTML
        const html = generateBase64HTML({
          id: _id,
          storyContents: sortedStoryContentsWithDisabledClick,
          title,
          description: description || '',
          hasGallery: true,
          thumbnail: thumbnail || '',
          options: {
            pdf: true,
            noJS: true
          },
          fonts,
          margins,
          storyMarginsEnabled,
          hasFootNotes,
          storyCustomCss: customCssCode,
          publicationCustomCss: publication?.customCssCode || story.folder?.publication?.customCssCode,
          isFollowable,
          progressTrackerSettings,
          isExportFromSetupPdf: true,
          enlargeFontsForPdf: enlargeFonts
        });

        if (!html) {
          throw new Error('Failed to generate HTML content');
        }

        // 8. Call PDF export API (watermark not supported by content-export-service)
        const response = await postJson<IPdfExportResponse>(
          '/api/content_export_service/v1/pdf',
          {
            showMargins: !storyMarginsEnabled,
            htmlEncoded: html,
            displayDate,
            displayPageNumber,
            startingPage,
            displayTitle,
            enlargeFonts
            // Note: watermark is applied on frontend after PDF generation
          },
          controller.signal
        );

        // 9. Apply watermark (if provided) and download the PDF
        if (response?.pdf) {
          // Apply watermark on frontend and download
          // response.pdf is base64 encoded string from content-export-service
          await addWatermarkAndDownload(response.pdf, watermark, title);

          setIsExporting(false);
          onSuccess?.();
          return { success: true };
        }

        throw new Error('Failed to generate PDF: API response missing PDF data');
      } catch (err) {
        setIsExporting(false);

        // Check if it's an abort error - ignore silently
        const isAbortError =
          (err instanceof Error && err.name === 'AbortError') ||
          (err instanceof DOMException && err.name === 'AbortError');

        if (isAbortError) {
          return { success: false };
        }

        // Handle real errors
        const errorObj = err instanceof Error ? err : new Error('An unknown error occurred during PDF export');

        toggleToast({
          title: 'Failed to export PDF',
          body: errorObj.message,
          severity: 'error'
        });

        onError?.(errorObj);
        return { success: false };
      }
    },
    [onSuccess, onError, toggleToast]
  );

  // Cancel current request
  const cancelExport = useCallback(() => {
    if (controllerRef.current) {
      controllerRef.current.abort();
      controllerRef.current = null;
    }
    setIsExporting(false);
  }, []);

  return {
    exportPdf,
    isExporting,
    cancelExport
  };
}
